import { useStepperContext } from '@folio/components/layout/StepperLayout/useStepperContext';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { getDocumentCategoryLabel } from '@folio/utils/getDocumentCategoryLabel';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  policyControllerListPolicyDocumentsQueryOptions,
  policyDocumentControllerGetPolicyDocumentConformityQueryOptions,
} from 'korint-sdk';
import { AlertTriangleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FunnelStepHeader } from '../../components/layout/FunnelStepHeader';
import { StepperFooter } from '../../components/navigation/stepper-footer/stepper-footer';
import { Alert } from '../../components/ui/alert';
import { DocumentsUploadPolicyModule } from '../../modules/DocumentsUploadPolicyModule/DocumentsUploadPolicyModule';
import {
  canDeleteStopDocuments,
  missingStopDocuments,
  rejectedStopDocuments,
} from './stop-documents';
import { StopFunnelGuard } from './StopFunnelGuard';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function DocumentsStep({ firstStepPath }: { firstStepPath: string }) {
  return (
    <StopFunnelGuard firstStepPath={firstStepPath}>
      {draft => (
        <StopDocuments
          branchId={draft.branchId}
          requiredCategories={draft.requiredDocuments}
          isAwaitingValidation={draft.isAwaitingValidation}
        />
      )}
    </StopFunnelGuard>
  );
}

function StopDocuments({
  branchId,
  requiredCategories,
  isAwaitingValidation,
}: {
  branchId: string;
  requiredCategories: string[];
  isAwaitingValidation: boolean;
}) {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();
  const { next } = useStepperContext();
  const needsProof = requiredCategories.length > 0;

  // Broker-side roles do not all carry POLICY_ADMIN, so read the grant rather than infer it.
  const { can } = useUserPermissions({
    productId: policy.productId,
    actions: ['DELETE_POLICY_DOCUMENTS'],
  });

  const { data: documents } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policy.id, { branchId }),
    enabled: needsProof,
    select: data =>
      data.data.map(({ id, category }) => ({
        id,
        category,
      })),
  });

  const { data: conformity } = useQuery({
    ...policyDocumentControllerGetPolicyDocumentConformityQueryOptions(policy.id, { branchId }),
    enabled: needsProof,
  });

  const { data: publicConfig } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    enabled: needsProof,
  });

  const conformities = conformity?.conformities ?? [];
  const missing = missingStopDocuments({
    requiredCategories,
    documents: documents ?? [],
    conformities,
  });
  const refusals = rejectedStopDocuments({ requiredCategories, conformities });
  const canDelete = canDeleteStopDocuments({
    hasDeletePermission: can.DELETE_POLICY_DOCUMENTS,
    isAwaitingValidation,
  });

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={t('stopFunnel.documents.title')}
        subtitle={t(needsProof ? 'stopFunnel.documents.subtitle' : 'stopFunnel.documents.none')}
      />

      {refusals.map(({ documentId, category, reason }) => (
        <Alert key={documentId} variant="destructive" className="flex items-start gap-2">
          <AlertTriangleIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
          <Text inline size="sm">
            {t('stopFunnel.documents.refused', {
              category: getDocumentCategoryLabel(category, publicConfig?.policyholderDocuments, t),
              reason,
            })}
          </Text>
        </Alert>
      ))}

      {needsProof && (
        <DocumentsUploadPolicyModule
          displayContext="policyholder"
          branchId={branchId}
          hideAcceptAllButton
          canDeleteDocuments={canDelete}
          documentsLists={requiredCategories.map(category => ({ category, required: true }))}
        />
      )}

      <StepperFooter onNext={next} nextButtonEnabled={missing.length === 0} />
    </Stack>
  );
}
